import api from "../api";
import { readinessTag } from "./integrationsBeta";

export interface WebhookTrigger {
  id: number;
  integration: number;
  provider: string;
  name: string;
  connection: number;
  url_token: string;
  is_active: boolean;
  verification_code: string;
  webhook_url: string;
  created_at: string;
  updated_at: string;
}

type ListResponse<T> = T[] | { results: T[] };

const tag = (integrationId: number | "LIST") => ({
  type: "WebhookTrigger" as const,
  id: integrationId,
});

export const webhookTriggersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getWebhookTriggers: builder.query<WebhookTrigger[], number>({
      query: (integrationId) => ({
        url: "/integration-platform/webhook-triggers/",
        method: "GET",
        params: { integration: integrationId },
      }),
      transformResponse: (resp: ListResponse<WebhookTrigger>) =>
        Array.isArray(resp) ? resp : resp.results,
      providesTags: (_result, _err, integrationId) => [tag(integrationId)],
    }),

    createWebhookTrigger: builder.mutation<
      WebhookTrigger,
      {
        integration: number;
        provider: string;
        connection: number;
        name?: string;
        is_active?: boolean;
      }
    >({
      query: (body) => ({
        url: "/integration-platform/webhook-triggers/",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _err, { integration }) => [
        tag(integration),
        readinessTag(integration),
      ],
    }),

    updateWebhookTrigger: builder.mutation<
      WebhookTrigger,
      {
        id: number;
        integration: number;
        name?: string;
        is_active?: boolean;
      }
    >({
      query: ({ id, integration, ...body }) => ({
        url: `/integration-platform/webhook-triggers/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _err, { integration }) => [
        tag(integration),
        readinessTag(integration),
      ],
    }),

    rotateWebhookTrigger: builder.mutation<
      WebhookTrigger,
      { id: number; integration: number }
    >({
      query: ({ id }) => ({
        url: `/integration-platform/webhook-triggers/${id}/rotate-url/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, { integration }) => [tag(integration)],
    }),

    deleteWebhookTrigger: builder.mutation<
      unknown,
      { id: number; integration: number }
    >({
      query: ({ id }) => ({
        url: `/integration-platform/webhook-triggers/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, { integration }) => [
        tag(integration),
        readinessTag(integration),
      ],
    }),

    redriveWebhookDlq: builder.mutation<{ task_handle: string }, void>({
      query: () => ({
        url: "/integration-platform/webhook-triggers/redrive-dlq/",
        method: "POST",
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetWebhookTriggersQuery,
  useCreateWebhookTriggerMutation,
  useUpdateWebhookTriggerMutation,
  useDeleteWebhookTriggerMutation,
  useRotateWebhookTriggerMutation,
  useRedriveWebhookDlqMutation,
} = webhookTriggersApi;
